import { Column, DataGrid, Editing, FilterRow } from 'devextreme-react/data-grid';
import Tooltip from 'devextreme-react/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration-v3';
import { ButtonOnExporting } from '../../utils/DataGridExporter';
import { formatDateToDisplay } from '../../utils/dateUtils';
import ConfirmationModal from './modals/ConfirmationModal';
import UndeliveredChassisModal from './modals/UndeliveredChassisModal';
// Teslimat durumu için sabit değerler
const DELIVERY_STATUS = {
    NOT_DELIVERED: 0,    // Teslim edilmemiş
    PARTIAL: 1,         // Kısmen teslim edilmiş
    COMPLETED: 2        // Tamamen teslim edilmiş
};

// Durum filtresi için seçenekler
const deliveryStatusOptions = [
    { id: DELIVERY_STATUS.NOT_DELIVERED, text: 'Bekliyor' },
    { id: DELIVERY_STATUS.PARTIAL, text: 'Kısmi' },
    { id: DELIVERY_STATUS.COMPLETED, text: 'Tamam' }
];

// Durum göstergesi için daire komponenti
const StatusCircle = ({ status, value }) => {
    let color;

    switch (status) {
        case DELIVERY_STATUS.NOT_DELIVERED:
            color = '#ef4444';
            break;
        case DELIVERY_STATUS.PARTIAL:
            color = '#f59e0b';
            break;
        case DELIVERY_STATUS.COMPLETED:
            color = '#22c55e';
            break;
        default:
            color = '#gray';
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block'
            }} />
            <span style={{ fontSize: '13px' }}>{value}</span>
        </div>
    );
};

const TeslimBilgisi = () => {
    const { hasRole } = useAuth(26)

    // State tanımlamaları
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isUndeliveredModalOpen, setIsUndeliveredModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedGrupajForCancel, setSelectedGrupajForCancel] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [savingGrupajNo, setSavingGrupajNo] = useState(null);
    const gridRef = useRef(null);
    const getUserId = () => {
        return parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'));
    };
    // Sayfa yüklendiğinde verileri getir
    useEffect(() => {
        fetchData();
    }, []);

    // Veri çekme fonksiyonu
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await api.post('/getAllGroupagesForAgacliKapiCikis', {
                shipping_start: "2020-03-01",
                shipping_end: "2099-03-31",
                employee_id: getUserId()
            });

            const formattedData = response.data.map(item => ({
                grupajNo: item.groupage_no,
                kapiCikisTarihi: formatDateToDisplay(item.door_exit_date),
                plaka: item.plate_number,
                sofor: item.driver_name,
                teslimTarihi: formatDateToDisplay(item.delivery_date),
                saseDurum: `${item.delivered_chassis_count}/${item.total_chassis_count}`,
                delivered_chassis_count: parseInt(item.delivered_chassis_count),
                total_chassis_count: parseInt(item.total_chassis_count),
                isLocked: item.delivery_date ? true : false
            }));

            setDataSource(formattedData);
        } catch (error) {
            toast.error(error.message ? error.message.replace("failed to get groupages: failed to get groupages for Agacli Kapi Cikis: ", "Teslim Bilgisi: ") : "Teslim Bilgisi: Hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    // İptal işlemi
    const handleIptal = async (data) => {
        if (isProcessing) return;
        setSelectedGrupajForCancel(data);
        setIsConfirmModalOpen(true);
    };

    // İptal onayı
    const confirmIptal = async () => {
        if (isProcessing) return;
        try {
            setIsProcessing(true);
            const response = await api.post('/deleteGroupageDeliveryDate', {
                groupage_no: selectedGrupajForCancel.grupajNo,
                update_user_id: getUserId(),
                employee_id: getUserId()
            });

            if (response.data.success) {
                toast.success(response.data.message);
                await fetchData();
            } else {
                let message = response.data.error || response.data.message || 'İşlem başarısız';
                toast.error(message);
            }
        } catch (error) {
            let message = error.message ? error.message.replace("failed to delete delivery date: ", "Teslim Bilgisi İptali: ") : "Teslim Bilgisi İptali: Hata oluştu";
            console.error('Error canceling delivery info:', error);
            toast.error(message);
        } finally {
            setIsProcessing(false);
            setIsConfirmModalOpen(false);
            setSelectedGrupajForCancel(null);
        }
    };

    // Satır güncelleme işlemi
    const onRowUpdating = async (e) => {
        if (e.data.teslimTarihi === "" || e.data.teslimTarihi === null) {
            toast.error("Teslim tarihi boş bırakılamaz");
            return;
        }
        try {
            const data = { teslimTarihi: e.data.teslimTarihi, grupajNo: e.data.grupajNo };
            setSavingGrupajNo(data.grupajNo);

            if (e.data.teslimTarihi) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const inputDate = new Date(e.data.teslimTarihi);
                inputDate.setHours(0, 0, 0, 0);

                if (inputDate > today) {
                    e.cancel = true;
                    toast.error('Teslim tarihi bugünden ileri bir tarih olamaz');
                    setSavingGrupajNo(null);
                    return;
                }

                const year = inputDate.getFullYear();
                const month = String(inputDate.getMonth() + 1).padStart(2, '0');
                const day = String(inputDate.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;

                try {
                    const response = await api.post('/updateGroupageDeliveryDate', {
                        groupage_no: data.grupajNo,
                        delivery_date: formattedDate,
                        update_user_id: getUserId(),
                        employee_id: getUserId()
                    });

                    if (!response.data.success) {
                        e.cancel = true;
                        toast.error('Teslim bilgisi kaydedilemedi');
                        return;
                    }
                    let message = response.data.error || response.data.message || 'İşlem başarısız';
                    toast.success(message);
                    // fetchData();
                    setDataSource(prev => prev.map(item =>
                        item.grupajNo === data.grupajNo
                            ? {
                                ...item, teslimTarihi: formattedDate, isLocked: true,
                                delivered_chassis_count: parseInt(item.total_chassis_count),
                                total_chassis_count: parseInt(item.total_chassis_count),
                                saseDurum: `${item.total_chassis_count}/${item.total_chassis_count}`
                            }
                            : item
                    ));
                } catch (error) {
                    e.cancel = true;
                    console.error('Error saving delivery info:', error);
                    toast.error('Teslim bilgisi kaydedilirken hata oluştu');
                } finally {
                    setSavingGrupajNo(null);
                }
            }
        } catch (error) {
            e.cancel = true;
            console.error('Error in row updating:', error);
            toast.error('İşlem sırasında bir hata oluştu');
            setSavingGrupajNo(null);
        }
    };

    // Satır renklendirme işlemi
    const onRowPrepared = (e) => {
        if (e.rowType === 'data') {
            const deliveredCount = parseInt(e.data.delivered_chassis_count);
            const totalCount = parseInt(e.data.total_chassis_count);

            if (deliveredCount === 0) {
                // e.rowElement.style.backgroundColor = '#fef2f2';
            } else if (deliveredCount < totalCount) {
                e.rowElement.style.backgroundColor = 'rgb(255 251 215)';
            } else if (deliveredCount === totalCount && totalCount > 0) {
                e.rowElement.style.backgroundColor = 'rgb(201 237 212)';
            }

            const cells = e.rowElement.getElementsByTagName('td');
            for (let i = 0; i < cells.length; i++) {
                cells[i].style.backgroundColor = e.rowElement.style.backgroundColor;
            }
        }
    };

    // Durum hesaplama fonksiyonları
    const calculateCustomStatus = (rowData) => {
        const deliveredCount = parseInt(rowData.delivered_chassis_count);
        const totalCount = parseInt(rowData.total_chassis_count);
        return getDeliveryStatus(deliveredCount, totalCount);
    };

    const getDeliveryStatus = (deliveredCount, totalCount) => {
        if (deliveredCount === 0) return DELIVERY_STATUS.NOT_DELIVERED;
        if (deliveredCount < totalCount) return DELIVERY_STATUS.PARTIAL;
        return DELIVERY_STATUS.COMPLETED;
    };

    const customStatusFilterValue = (rowData) => {
        return calculateCustomStatus(rowData);
    };

    const calculateStatusFilterExpression = (filterValue) => {
        return [
            function (data) {
                const deliveredCount = parseInt(data.delivered_chassis_count);
                const totalCount = parseInt(data.total_chassis_count);
                const status = getDeliveryStatus(deliveredCount, totalCount);
                return status === filterValue;
            },
            '=',
            true
        ];
    };

    // Durum hücresi render fonksiyonu
    const renderDeliveryStatus = (data) => {
        const deliveredCount = parseInt(data.data.delivered_chassis_count);
        const totalCount = parseInt(data.data.total_chassis_count);
        const status = getDeliveryStatus(deliveredCount, totalCount);

        return <StatusCircle status={status} value={data.data.saseDurum} />;
    };

    // İşlemler kolonu render fonksiyonu
    const renderActions = (data) => {
        let colored = !data.data.isLocked && (data.data.teslimTarihi !== "");

        return (
            <div className="action-buttons action-links" style={{ height: "29px" }}>
                <span
                    className={`mr-2 action-link ${data.data.isLocked ? 'disabled' : ''}`}
                    onClick={() => !data.data.isLocked && onRowUpdating(data)}
                    style={{
                        cursor: data.data.isLocked ? 'not-allowed' : 'pointer',
                        opacity: data.data.isLocked ? 0.5 : 1,
                        color: colored ? 'red' : data.data.isLocked ? '#rgb(97 97 97)' : '#0056b3',
                        width: "65px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    {savingGrupajNo === data.data.grupajNo ? (
                        <i className="fa fa-spinner fa-spin mr-1"></i>
                    ) : null}
                    Kaydet
                </span>

                <span
                    className={`action-link ${!data.data.isLocked ? 'disabled' : ''}`}
                    onClick={() => handleIptal(data.data)}
                    disabled={isProcessing || !data.data.teslimTarihi}
                    style={{
                        padding: "3px 9px",
                        width: "140px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    title="Teslim Bilgisi İptal"
                >
                    {isProcessing && selectedGrupajForCancel?.grupajNo === data.data.grupajNo ? (
                        <i className="fa fa-spinner fa-spin mr-1"></i>
                    ) : null}
                    Teslim Bilgisi İptal
                </span>
            </div>
        );
    };

    return (
        <>
            <Breadcrumb current="Teslim Bilgisi" />
            <div className="p-4 bg-light min-vh-100">
                <Card>
                    <CardBody>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 gap-md-0 mb-0">
                            <div className="alert alert-info d-flex align-items-center w-100 w-md-auto text-center text-md-start m-0 teslim-flex-2-md">
                                <i className="fa fa-info-circle "></i>
                                İlk önce grupajın diğer araçları için teslim tarihi girilmeli, sonra şase bazında bildirim yapılmalıdır
                            </div>

                            <div className="d-flex justify-content-end flex-wrap w-100 w-md-auto gap-1 teslim-flex-1-md">
                                <button
                                    className="top-buttons d-flex align-items-center justify-content-center"
                                    title="Yenile"
                                    style={{
                                        border: "1px solid #dee2e6",
                                        background: "#ffffff",
                                        color: "#6c757d",
                                        padding: "8px",
                                        minWidth: "40px",
                                        cursor: "pointer",
                                        borderRadius: "4px"
                                    }}
                                    onClick={fetchData}
                                    disabled={loading}
                                >
                                    <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
                                </button>
                                <button
                                    style={{
                                        padding: "8px",
                                        minWidth: "40px",
                                        borderRadius: "4px"
                                    }}
                                    onClick={(e) => ButtonOnExporting(e, gridRef, "Teslim Bilgileri", "Teslim Bilgileri")}
                                    className="btn btn-success d-flex align-items-center justify-content-center top-buttons ml-1"
                                    title="Excel">
                                    <i className="fa fa-file-excel-o"></i>
                                </button>
                                {hasRole("tedarikci_teslim_write") && <button
                                    className="btn btn-danger d-flex align-items-center justify-content-center top-buttons flex-grow-1 flex-md-grow-0 ml-1"
                                    onClick={() => setIsUndeliveredModalOpen(true)}
                                >
                                    <i className="fa fa-exclamation-circle mr-2"></i>
                                    Teslim Edilemeyen Şase Bildir
                                </button>}
                            </div>
                        </div>

                        <div className="table-responsive">
                            <DataGrid
                                ref={gridRef}
                                dataSource={dataSource}
                                showBorders={true}
                                columnAutoWidth={true}
                                rowAlternationEnabled={false}
                                allowColumnResizing={true}
                                showColumnLines={true}
                                showRowLines={true}
                                //  onRowUpdating={onRowUpdating}
                                onRowPrepared={onRowPrepared}
                                keyExpr="grupajNo"
                                loadPanel={{
                                    enabled: loading,
                                    text: "Yükleniyor...",
                                    showIndicator: true,
                                    showPane: true,
                                }}
                                noDataText="Gösterilecek veri bulunamadı"
                                className='teslim-bilgisi-grid'
                            >
                                <FilterRow visible={true} />
                                <Editing
                                    mode="cell"
                                    allowUpdating={true}
                                />
                                <Column dataField="grupajNo" caption="Grupaj No" alignment="left" allowEditing={false} width={145} />
                                <Column
                                    dataField="kapiCikisTarihi"
                                    caption="Kapı Çıkış Tarihi"
                                    alignment="left"
                                    allowEditing={false}
                                    width={130}
                                    dataType="date"
                                    format="dd.MM.yyyy"
                                />
                                <Column dataField="plaka" caption="Plaka" alignment="left" allowEditing={false} />
                                <Column dataField="sofor" caption="Şoför" alignment="left" allowEditing={false} />
                                <Column
                                    dataField="saseDurum"
                                    caption="Durum"
                                    alignment="center"
                                    allowEditing={false}
                                    width={76}
                                    cellRender={renderDeliveryStatus}
                                    calculateFilterExpression={calculateStatusFilterExpression}
                                    calculateCellValue={customStatusFilterValue}
                                    lookup={{
                                        dataSource: deliveryStatusOptions,
                                        valueExpr: 'id',
                                        displayExpr: 'text'
                                    }}
                                />
                                <Column
                                    dataField="teslimTarihi"
                                    caption="Teslim Tarihi"
                                    dataType="date"
                                    allowEditing={hasRole("tedarikci_teslim_write")}
                                    setCellValue={(e, value) => {
                                        if (value === null) e.teslimTarihi = "";
                                        else e.teslimTarihi = value;
                                    }}
                                    headerCellRender={(e) => {
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <i className="fa fa-info-circle" id="teslim-tarihi-info"></i> &nbsp;
                                                <Tooltip
                                                    target={`#teslim-tarihi-info`}
                                                    showEvent="mouseenter"
                                                    hideEvent="mouseleave"
                                                    hideOnOutsideClick={false}
                                                >
                                                    <div style={{
                                                        backgroundColor: '#f9f9f9',
                                                        padding: '10px',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        color: '#333',
                                                        border: '1px solid #ddd',
                                                        margin: "0px"
                                                    }}>
                                                        Teslim tarihi girmek istediğiniz hücreye tıklayın, tarih seçin ve 'Kaydet' butonuna basın.
                                                    </div>
                                                </Tooltip>
                                                Teslim Tarihi
                                            </div>
                                        )
                                    }}
                                    format="dd.MM.yyyy"
                                    editorOptions={{
                                        type: 'date',
                                        max: new Date(),
                                        displayFormat: "dd.MM.yyyy",
                                        useMaskBehavior: true,
                                        dateSerializationFormat: "yyyy-MM-dd"
                                    }}
                                    alignment="left"
                                />
                                {
                                    hasRole("tedarikci_teslim_write") &&
                                    <Column caption="İşlemler" cellRender={renderActions} width={215} alignment="center" allowEditing={false} />}
                            </DataGrid>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                toggle={() => setIsConfirmModalOpen(false)}
                title="Teslim Bilgisi İptali"
                message={
                    <div>
                        <span className="fw-medium">Grupaj No: </span>
                        <b>{selectedGrupajForCancel?.grupajNo}</b>
                        <span className="ms-2"> teslim bilgisi iptal edilecektir.</span>
                    </div>
                }
                onConfirm={confirmIptal}
                isProcessing={isProcessing}
                confirmText="Onayla"
                cancelText="İptal"
            />

            <UndeliveredChassisModal
                isOpen={isUndeliveredModalOpen}
                toggle={() => setIsUndeliveredModalOpen(false)}
                refreshData={fetchData}
            />

            <style jsx>{`
                .top-buttons {
                    padding: 8px 10px !important;
                }
                .fa-spin {
                    animation: fa-spin 1s infinite linear;
                }
                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @media (max-width: 768px) {
                    .teslim-bilgisi-grid {
                        min-width: 800px;
                    }
                    
                    .action-buttons {
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                    }
                    
                    .action-buttons button {
                        width: 100%;
                        margin: 0 !important;
                    }
                    
                    [class*="dx-tooltip"] {
                        max-width: 90vw !important;
                    }
                }
                    .action-links {
                    display: flex;
                    gap: 0;
                    justify-content: center;
                    width: 100%;
                }
                .action-link {
                    color: #0056b3;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: color 0.2s;
                    padding: 0 8px;
                    position: relative;
                    min-width: 60px;
                    text-align: center;
                }
                .action-link:not(:last-child)::after {
                    content: '|';
                    position: absolute;
                    right: -2px;
                    color: #dee2e6;
                    font-size: 12px;
                    font-weight: 900;
                }
                .action-link:hover {
                    color: #003d82;
                    text-decoration: underline;
                }
                .action-link.disabled {
                    color: #999;
                    pointer-events: none;
                }
                .dx-datagrid-revert-tooltip {  
                     display: none;  
                    } 
                @media (min-width: 768px) {
                    .teslim-flex-2-md {
                        flex: 2;
                    }
                    .teslim-flex-1-md {
                        flex: 1;
                    }
                }
                @media (max-width: 480px) {
                    .dx-datagrid-headers .dx-datagrid-table .dx-row > td,
                    .dx-datagrid-rowsview .dx-row > td {
                        padding: 5px;
                    }
                }
            `}</style>
        </>
    );
};

export default TeslimBilgisi; 